import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Group, SimpleGrid, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { Alert, EmptyState, PageHeader, TableShell, type ViewMode } from '@repletoryreach/sdk-ui/data-display';
import { isApiError, type admin } from '@repletoryreach/sdk-api';
import { useAiModels } from '../../../data/useAiModels';
import { useSkuRates } from '../../../data/useSkuRates';
import { StatCard } from '../../../components/StatCard';
import { usePersistedState } from '../../../lib/usePersistedState';
import { formatInteger, formatMinor } from '../../../lib/format';
import { useT } from '../../../i18n/I18nProvider';
import { ViewToggle } from '../components/ViewToggle';
import { IconChart, IconGlobe, IconLayers, IconWallet } from '../components/icons';
import { KIND_ORDER, modelPrice, sortModels, type AiModelKind } from './aiModels';
import { AiModelsTable } from './components/AiModelsTable';
import { AiModelsList } from './components/AiModelsList';
import { AiModelEditDrawer } from './components/AiModelEditDrawer';
import { AiModelTestModal } from './components/AiModelTestModal';
import { useModelSwitch } from './components/useModelSwitch';
import '../components/billing.css';
import './ai-models.css';

const STAT_COLS = { base: 1, sm: 2, lg: 4 };

/** A phone opens on the list the first time; after that the operator's own choice is remembered. */
const FIRST_VIEW: ViewMode = typeof window !== 'undefined' && window.matchMedia?.('(max-width: 720px)').matches ? 'list' : 'table';

/**
 * The AI models businesses may choose in the AI API and on the AI tools page (owner, 14 October 2026: "a short list
 * you control"). Each one is charged to the business's credits at its plan's rate, from the price book rows its SKUs
 * name. A model is switched on only after Test has had it answer, and a model that runs outside South Africa says so
 * before it goes on.
 */
export default function AiModelsPage() {
  const t = useT();
  const navigate = useNavigate();
  const modelsQ = useAiModels();
  const ratesQ = useSkuRates();
  const [view, setView] = usePersistedState<ViewMode>('admin.ai-models.view', FIRST_VIEW);
  const [editing, setEditing] = useState<admin.AdminAiModel | null>(null);
  const [testing, setTesting] = useState<admin.AdminAiModel | null>(null);
  // What each Test said in this visit, so the switch-on confirmation can say whether the model answered.
  const [tests, setTests] = useState<Record<string, admin.AiModelTest>>({});

  const models = useMemo(() => sortModels(modelsQ.data?.data ?? []), [modelsQ.data]);
  const rates = useMemo(() => ratesQ.data?.data ?? [], [ratesQ.data]);
  const priceOf = useCallback((m: admin.AdminAiModel) => modelPrice(m, rates), [rates]);
  const ratesFailed = Boolean(ratesQ.error);

  const { busy, toggle } = useModelSwitch({ priceOf: (m) => (ratesFailed ? null : priceOf(m)), tests });

  const groups = useMemo(() => {
    const byKind = new Map<AiModelKind, admin.AdminAiModel[]>();
    for (const m of models) byKind.set(m.kind, [...(byKind.get(m.kind) ?? []), m]);
    // A kind the page does not know yet still shows, after the four it does.
    const known = KIND_ORDER.filter((k) => byKind.has(k));
    const unknown = Array.from(byKind.keys()).filter((k) => !KIND_ORDER.includes(k));
    return [...known, ...unknown].map((kind) => ({ kind, models: byKind.get(kind)! }));
  }, [models]);

  const stats = useMemo(() => {
    const on = models.filter((m) => m.enabled);
    return {
      on: on.length,
      total: models.length,
      calls: models.reduce((sum, m) => sum + m.calls_last30_days, 0),
      charged: models.reduce((sum, m) => sum + m.charged_last30_days_minor, 0),
      outsideOn: on.filter((m) => m.outside_south_africa).length,
      outsideMarked: models.filter((m) => m.outside_south_africa).length
    };
  }, [models]);

  const denied = isApiError(modelsQ.error) && modelsQ.error.status === 403;
  const rowProps = {
    priceOf,
    ratesFailed,
    tests,
    busy,
    onToggle: toggle,
    onTest: (m: admin.AdminAiModel) => setTesting(m),
    onEdit: (m: admin.AdminAiModel) => setEditing(m)
  };

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('billing.aiModels.eyebrow')}
        title={t('billing.aiModels.title')}
        description={t('billing.aiModels.description')}
        actions={
          <Group gap="sm">
            <Button size="sm" variant="outline" onClick={() => navigate('/billing/sku-rates')}>{t('billing.aiModels.openPrices')}</Button>
            <ViewToggle value={view === 'cards' ? 'table' : view} onChange={setView} />
          </Group>
        }
      />

      <SimpleGrid cols={STAT_COLS} spacing="md">
        <StatCard
          withRule tone="success" icon={<IconLayers />}
          label={t('billing.aiModels.stat.on')}
          value={formatInteger(stats.on)}
          hint={t('billing.aiModels.stat.onHint', { total: formatInteger(stats.total) })}
          loading={modelsQ.isLoading}
        />
        <StatCard
          withRule tone="primary" icon={<IconChart />}
          label={t('billing.aiModels.stat.calls')}
          value={formatInteger(stats.calls)}
          hint={t('billing.aiModels.stat.callsHint')}
          loading={modelsQ.isLoading}
        />
        <StatCard
          withRule tone="info" icon={<IconWallet />}
          label={t('billing.aiModels.stat.charged')}
          value={formatMinor(stats.charged, 'ZAR')}
          hint={t('billing.aiModels.stat.chargedHint')}
          loading={modelsQ.isLoading}
        />
        <StatCard
          withRule tone={stats.outsideOn > 0 ? 'warning' : 'neutral'} icon={<IconGlobe />}
          label={t('billing.aiModels.stat.outside')}
          value={formatInteger(stats.outsideOn)}
          hint={stats.outsideOn > 0
            ? t('billing.aiModels.stat.outsideOnHint')
            : t('billing.aiModels.stat.outsideHint', { count: formatInteger(stats.outsideMarked) })}
          loading={modelsQ.isLoading}
        />
      </SimpleGrid>

      {ratesFailed && (
        <Alert color="warning">
          <Group justify="space-between" gap="sm" wrap="wrap">
            <Text role="bodySmall">{t('billing.aiModels.ratesError')}</Text>
            <Button size="xs" variant="outline" onClick={() => void ratesQ.refetch()}>{t('action.retry')}</Button>
          </Group>
        </Alert>
      )}

      <TableShell
        totalLabel={t('billing.aiModels.showingCount', { on: formatInteger(stats.on), total: formatInteger(stats.total) })}
        // The price is part of every row, so the rows wait for the price book rather than show a gap that fills in.
        loading={modelsQ.isLoading || ratesQ.isLoading}
        isEmpty={!modelsQ.error && models.length === 0}
        empty={<EmptyState title={t('billing.aiModels.empty')} description={t('billing.aiModels.emptyHint')} />}
        error={
          modelsQ.error ? (
            <Alert color="error">
              <Stack gap="sm">
                <Text role="bodySmall">{denied ? t('billing.aiModels.denied') : t('billing.aiModels.loadError')}</Text>
                {!denied && (
                  <Group>
                    <Button size="xs" variant="outline" onClick={() => void modelsQ.refetch()}>{t('action.retry')}</Button>
                  </Group>
                )}
              </Stack>
            </Alert>
          ) : null
        }
      >
        <Stack gap="lg">
          {groups.map(({ kind, models: rows }) => (
            <Stack key={kind} gap="sm" data-testid={`ai-models-${kind}`}>
              <Stack gap={2}>
                <Group gap="xs" align="center">
                  <Title order={5}>{t(`billing.aiModels.kind.${kind}`, kind)}</Title>
                  <Text role="caption" c="var(--rr-color-text-muted)">
                    {t('billing.aiModels.kindCount', { on: formatInteger(rows.filter((m) => m.enabled).length), total: formatInteger(rows.length) })}
                  </Text>
                </Group>
                <Text role="bodySmall" c="var(--rr-color-text-muted)">{t(`billing.aiModels.kindHint.${kind}`, '')}</Text>
              </Stack>
              {view === 'list'
                ? <AiModelsList models={rows} {...rowProps} />
                : <AiModelsTable models={rows} {...rowProps} />}
            </Stack>
          ))}
          <Text role="caption" c="var(--rr-color-text-muted)">{t('billing.aiModels.pricesNote')}</Text>
        </Stack>
      </TableShell>

      <AiModelEditDrawer model={editing} onClose={() => setEditing(null)} />
      <AiModelTestModal
        // The row as it is now, so a model switched on elsewhere no longer offers "Switch on".
        model={testing ? models.find((m) => m.code === testing.code) ?? testing : null}
        onClose={() => setTesting(null)}
        onResult={(code, result) => setTests((prev) => ({ ...prev, [code]: result }))}
        onSwitchOn={(m) => { setTesting(null); toggle(m, true); }}
        // What a picture test costs the platform, from the price book's cost column.
        testCost={(() => {
          const unit = testing && testing.kind === 'image' && !ratesFailed ? priceOf(testing).unit : undefined;
          return unit ? formatMinor(unit.cost_minor, unit.currency) : null;
        })()}
      />
    </Stack>
  );
}
